import { CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

// Native Shopify product/cart endpoints keep market, language, currency and stock authoritative.
const config = JSON.parse(document.getElementById('sc-cross-selling-config').textContent);
const root = window.Shopify.routes.root;
const fr = document.documentElement.lang.startsWith('fr');
const copy = fr ? {
  adding: 'Ajout en cours…', upgrading: 'Passage à la paire en cours…', checking: 'Vérification du panier en cours…',
  title: 'À porter avec', add: 'Ajouter', upgrade: 'Passer à la paire', extra: 'en plus',
  error: 'Impossible de modifier le panier. Vérifiez son contenu puis réessayez.',
  changed: 'Votre panier a changé. Vérifiez-le avant de réessayer.',
  recovery: 'La paire et la monoboucle peuvent être présentes. Vérifiez les quantités dans votre panier avant de payer.',
  added: 'Ajouté au panier', upgraded: 'La monoboucle a été remplacée par la paire.',
  unavailable: 'Ces suggestions sont momentanément indisponibles.'
} : {
  adding: 'Adding…', upgrading: 'Upgrading to a pair…', checking: 'Checking your cart…',
  title: 'Wear it with', add: 'Add', upgrade: 'Upgrade to a pair', extra: 'extra',
  error: 'Unable to update your cart. Check its contents and try again.',
  changed: 'Your cart changed. Please check it before trying again.',
  recovery: 'Both the pair and single may be in your cart. Check your quantities before checkout.',
  added: 'Added to cart', upgraded: 'The single earring has been replaced with the pair.',
  unavailable: 'These suggestions are temporarily unavailable.'
};
const products = new Map();
let busy = false;
// Capture also covers newly morphed checkout buttons while a mutation is unresolved.
const checkoutPending = () => document.documentElement.hasAttribute('data-sc-cart-pending') ||
  !!document.querySelector('cart-items-component[aria-busy="true"], product-form-component[aria-busy="true"]');
document.addEventListener('click', event => {
  if (checkoutPending() && event.target.closest('[name="checkout"], .additional-checkout-buttons, a[href*="/checkout"]')) {
    event.preventDefault(); event.stopImmediatePropagation();
  }
}, true);
document.addEventListener('submit', event => {
  if (checkoutPending() && (event.submitter?.name === 'checkout' || event.target.id === 'cart-form')) {
    event.preventDefault(); event.stopImmediatePropagation();
  }
}, true);
let currentCart;
let cartPromise;
async function timedFetch(url, options = {}, signal) {
  if (signal?.aborted) throw new Error(copy.error);
  const controller = new AbortController();
  let timer;
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  try {
    return await Promise.race([
      (async () => {
        const response = await fetch(url, { ...options, signal: controller.signal });
        // Include body reading in the timeout, not merely response headers.
        const text = await response.text();
        return { ok: response.ok, text };
      })(),
      new Promise((_, reject) => {
        timer = setTimeout(() => { controller.abort(); reject(new Error(copy.error)); }, 5000);
        controller.signal.addEventListener('abort', () => reject(new Error(copy.error)), { once: true });
      })
    ]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}
async function request(path, body, signal) {
  const response = await timedFetch(root + path, {
    method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
    headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined
  }, signal);
  const data = JSON.parse(response.text);
  if (!response.ok || data.status >= 400 || data.errors) throw new Error(data.description || copy.error);
  return data;
}
function readCart(signal) { return request('cart.js', undefined, signal); }
function sharedCart() {
  return cartPromise ||= readCart().finally(() => { cartPromise = null; });
}
function product(handle, fresh = false, signal) {
  if (signal) return request('products/' + encodeURIComponent(handle) + '.js', undefined, signal);
  if (fresh) products.delete(handle);
  if (!products.has(handle)) products.set(handle, request('products/' + encodeURIComponent(handle) + '.js').catch(error => {
    products.delete(handle); throw error;
  }));
  return products.get(handle);
}
async function eligibleUpgrade(handle, signal) {
  const response = await timedFetch(root + 'products/' + encodeURIComponent(handle) +
    '?section_id=sc-upgrade-eligibility', { cache: 'no-store', credentials: 'same-origin' }, signal);
  if (!response.ok) return false;
  const html = new DOMParser().parseFromString(response.text, 'text/html');
  const json = html.querySelector('[data-sc-upgrade-eligibility]')?.textContent;
  return json ? JSON.parse(json).eligible === true : false;
}
function base(handle) { return config.singles[handle] || handle; }
function flatten(items) {
  return (items || []).flatMap(item => [item, ...flatten(item.item_components), ...flatten(item.components)]);
}
function handles(cart) {
  const found = new Set();
  for (const item of flatten(cart.items)) {
    const handle = item.handle || config.productHandles[item.product_id] || config.variantHandles[item.variant_id || item.id];
    if (handle) found.add(base(handle));
    for (const component of config.components[handle] || []) found.add(base(component));
  }
  return found;
}
function singleLine(cart, key, handle) {
  const line = cart.items.find(item => item.key === key);
  if (!line || line.quantity !== 1 || line.handle !== handle || line.parent_relationship?.parent ||
      line.parent_relationship?.parent_key ||
      line.item_components?.length || line.selling_plan_allocation || Object.keys(line.properties || {}).length ||
      cart.items.filter(item => base(item.handle) === handle).length !== 1 ||
      Number(line.final_price) !== Number(line.original_price) || cart.total_discount > 0) return null;
  // A component in a separate native bundle is not an eligible single.
  for (const item of cart.items) {
    if (item.key === key) continue;
    if (flatten([item]).some(part => base(part.handle || config.variantHandles[part.variant_id || part.id]) === handle) ||
        (config.components[item.handle] || []).includes(handle)) return null;
  }
  return line;
}
function money(value, currency) {
  // Shopify Ajax monetary values use hundredths, including zero-decimal currencies.
  return new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency }).format(value / 100);
}
function node(tag, text, className) {
  const element = document.createElement(tag);
  if (text) element.textContent = text;
  if (className) element.className = className;
  return element;
}
async function sync() {
  currentCart = await readCart();
  const ids = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))];
  let sections;
  if (ids.length) {
    const url = new URL(location.href);
    url.searchParams.set('sections', ids.slice(0, 5).join(','));
    const response = await timedFetch(url, { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error(copy.error);
    sections = JSON.parse(response.text);
    if (ids.slice(0, 5).some(id => typeof sections[id] !== 'string')) throw new Error(copy.error);
  }
  // Await the actual rendered totals, not only the network response.
  if (sections) await Promise.all(ids.slice(0, 5).map(id => morphSection(id, sections[id], 'full', { injectStylesheet: true })));
  document.dispatchEvent(new CartUpdateEvent(currentCart, 'sc-cross-selling', {
    source: 'sc-cross-selling', itemCount: currentCart.item_count, sections
  }));
  return currentCart;
}
async function rollbackPair(token, signal) {
  try {
    const cart = await readCart(signal);
    const added = cart.items.filter(item => item.properties?._sc_pair_upgrade === token &&
      !item.parent_relationship?.parent && !item.parent_relationship?.parent_key);
    if (added.length !== 1) throw new Error(copy.recovery);
    await request('cart/change.js', { id: added[0].key, quantity: 0 }, signal);
    const after = await readCart(signal);
    if (after.items.some(item => item.properties?._sc_pair_upgrade === token)) throw new Error(copy.recovery);
  } catch { throw new Error(copy.recovery); }
}
async function upgrade(action, signal) {
  let cart = await readCart(signal);
  const original = singleLine(cart, action.key, action.handle);
  if (!original) throw new Error(copy.changed);
  if (!await eligibleUpgrade(action.handle, signal)) throw new Error(copy.changed);
  const pair = await product(config.pairs[action.handle], true, signal);
  const variant = pair.variants.find(item => item.available);
  if (!variant || String(variant.id) !== String(action.variant) ||
      variant.price - original.final_price !== action.delta || cart.currency !== action.currency) throw new Error(copy.changed);
  const token = crypto.randomUUID();
  // Add first. NEVER remove the customer's single on an unsuccessful/uncertain add.
  let addError;
  try {
    await request('cart/add.js', { items: [{ id: variant.id, quantity: 1, properties: { _sc_pair_upgrade: token } }] }, signal);
  } catch (error) { addError = error; }
  cart = await readCart(signal);
  const added = cart.items.filter(item => item.properties?._sc_pair_upgrade === token && item.handle === pair.handle && item.quantity === 1);
  if (added.length !== 1) throw new Error(addError?.message || copy.recovery);
  const unchanged = cart.items.find(item => item.key === original.key);
  const otherPairs = cart.items.filter(item => item.handle === pair.handle && item.key !== added[0].key);
  const otherSingles = cart.items.filter(item => item.handle === original.handle && item.key !== original.key && !item.parent_relationship);
  if (!unchanged || unchanged.quantity !== 1 || unchanged.final_price !== original.final_price || otherPairs.length || otherSingles.length ||
      added[0].final_price !== variant.price || cart.currency !== action.currency) {
    await rollbackPair(token, signal);
    throw new Error(copy.changed);
  }
  try {
    await request('cart/change.js', { id: original.key, quantity: 0 }, signal);
  } catch (error) {
    // A network timeout may hide a successful change. Inspect before compensating.
    const state = await readCart(signal);
    if (state.items.some(item => item.key === original.key)) {
      await rollbackPair(token, signal);
      throw error;
    }
  }
  const final = await readCart(signal);
  if (final.items.some(item => item.key === original.key)) {
    await rollbackPair(token, signal);
    throw new Error(copy.changed);
  }
  if (!final.items.some(item => item.properties?._sc_pair_upgrade === token && item.quantity === 1 && item.handle === pair.handle)) {
    // A separate tab removed the newly-added pair: restore the original, not a third piece.
    if (!handles(final).has(action.handle)) await request('cart/add.js', { items: [{ id: original.variant_id, quantity: 1 }] }, signal);
    throw new Error(copy.changed);
  }
}
async function perform(action, signal) {
  if (action.type === 'upgrade') return upgrade(action, signal);
  const cart = await readCart(signal);
  if (handles(cart).has(base(action.handle))) throw new Error(copy.changed);
  const p = await product(action.handle, true, signal);
  const available = p.variants.filter(variant => variant.available);
  if (available.length !== 1 || String(available[0].id) !== String(action.variant)) throw new Error(copy.changed);
  await request('cart/add.js', { items: [{ id: action.variant, quantity: 1 }] }, signal);
}
class SCCrossSelling extends HTMLElement {
  connectedCallback() {
    this.refresh();
    this.listener = () => this.refresh();
    document.addEventListener('cart:update', this.listener);
  }
  disconnectedCallback() { document.removeEventListener('cart:update', this.listener); }
  async refresh() {
    const generation = this.generation = (this.generation || 0) + 1;
    try {
      const cart = await sharedCart();
      if (!this.isConnected || generation !== this.generation) return;
      currentCart = cart;
      const excluded = handles(cart);
      const isPdp = this.dataset.surface === 'product';
      const sources = isPdp ? [base(this.dataset.handle)] : [...excluded];
      if (isPdp) excluded.add(base(this.dataset.handle));
      const candidates = [...new Set(sources.flatMap(handle => config.map[handle] || []))].filter(handle => !excluded.has(base(handle)));
      const fragment = document.createDocumentFragment();
      // At most one upgrade per surface, separate from the two complementary products.
      if (!isPdp) {
        for (const line of cart.items) {
          if (!config.pairs[line.handle] || !singleLine(cart, line.key, line.handle)) continue;
          if (!await eligibleUpgrade(line.handle)) continue;
          const p = await product(config.pairs[line.handle]);
          const v = p.variants.find(variant => variant.available);
          if (!v || v.price <= line.final_price) continue;
          const delta = v.price - line.final_price;
          fragment.append(this.card(p, cart.currency, {
            type: 'upgrade', handle: line.handle, key: line.key, variant: v.id, delta, currency: cart.currency
          }, copy.upgrade + ' · +' + money(delta, cart.currency), true));
          break;
        }
      }
      let count = 0;
      for (const handle of candidates.slice(0, 10)) {
        const p = await product(handle);
        const variants = p.variants.filter(variant => variant.available);
        if (!p.available || !variants.length || config.singles[handle]) continue;
        if (!count) fragment.append(node('h3', copy.title));
        fragment.append(this.card(p, cart.currency, variants.length === 1 ? {
          type: 'add', handle, variant: variants[0].id
        } : null, copy.add));
        if (++count === 2) break;
      }
      if (!this.isConnected || generation !== this.generation) return;
      this.replaceChildren(fragment);
      this.hidden = !this.children.length;
    } catch {
      if (!this.isConnected || generation !== this.generation) return;
      this.replaceChildren();
      this.hidden = true;
      this.dataset.loadError = copy.unavailable;
    }
  }
  card(p, currency, action, label, isUpgrade = false) {
    const card = node('div', '', 'sc-cross-selling__card');
    const link = node('a');
    link.href = root + 'products/' + encodeURIComponent(p.handle);
    if (p.featured_image) {
      const img = node('img');
      const url = new URL(p.featured_image, location.origin);
      url.searchParams.set('width', '144');
      img.src = url.href; img.alt = p.title; img.width = 64; img.height = 80; img.loading = 'lazy';
      link.append(img);
    }
    const body = node('div');
    const title = node('a', p.title); title.href = link.href;
    body.append(title);
    const selected = p.variants.find(v => String(v.id) === String(action?.variant)) || p.variants.find(v => v.available);
    body.append(node('span', money(selected.price, currency), 'sc-cross-selling__price'));
    if (action) {
      const button = node('button', label, 'sc-cross-selling__button');
      button.type = 'button';
      button.addEventListener('click', () => this.act(action, button));
      body.append(button);
    } else {
      const choose = node('a', fr ? 'Choisir une option' : 'Choose an option');
      choose.href = link.href; body.append(choose);
    }
    if (isUpgrade) card.classList.add('sc-cross-selling__upgrade');
    card.append(link, body);
    return card;
  }
  async act(action, button) {
    if (busy || checkoutPending()) return;
    busy = true;
    document.documentElement.dataset.scCartPending = 'true';
    const host = this.closest('dialog') || this.parentElement;
    const status = node('p', '', 'sc-cross-selling__status');
    status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    host.querySelectorAll('.sc-cross-selling__status').forEach(el => el.remove());
    host.append(status);
    status.textContent = action.type === 'upgrade' ? copy.upgrading : copy.adding;
    button.textContent = status.textContent;
    const controls = [...document.querySelectorAll('sc-cross-selling button, cart-items-component button, cart-items-component input, product-form-component button, product-form-component input, [name="checkout"]')];
    const enabled = controls.filter(el => !el.disabled);
    enabled.forEach(el => { el.dataset.scBusyDisabled = 'true'; el.disabled = true; });
    button.setAttribute('aria-busy', 'true');
    const controller = new AbortController();
    let deadline;
    try {
      // Never queue behind an unbounded browser Web Lock. Every write remains guarded by fresh cart keys.
      await Promise.race([
        perform(action, controller.signal),
        new Promise((_, reject) => {
          deadline = setTimeout(() => { controller.abort(); reject(new Error(copy.recovery)); }, 18000);
        })
      ]);
      status.textContent = action.type === 'upgrade' ? copy.upgraded : copy.added;
    } catch (error) {
      status.textContent = copy.error + (error.message && error.message !== copy.error ? ' ' + error.message : '');
    } finally {
      clearTimeout(deadline);
      controller.abort();
      // Keep checkout locked through confirmed cart + section reconciliation.
      let confirmed = false;
      try { await sync(); confirmed = true; } catch {
        status.textContent = copy.error;
        const retry = node('a', fr ? 'Recharger le panier' : 'Reload your cart');
        retry.href = root + 'cart';
        status.append(' ', retry);
      }
      for (const el of [...enabled, ...document.querySelectorAll('[data-sc-busy-disabled]')]) {
        if (el.isConnected && (confirmed || el.name !== 'checkout')) {
          el.disabled = false; delete el.dataset.scBusyDisabled;
        }
      }
      button.removeAttribute('aria-busy');
      button.textContent = action.type === 'upgrade' ? copy.upgrade : copy.add;
      if (confirmed) delete document.documentElement.dataset.scCartPending;
      busy = !confirmed;
      // Morphing can replace the original status; keep feedback in the current surface.
      if (!status.isConnected) {
        const target = document.querySelector('cart-drawer-component dialog[open] .cart-drawer__content') ||
          document.querySelector('sc-cross-selling[data-surface="cart"]') || this;
        target.append(status);
      }
      if (action.type === 'add' && this.dataset.surface === 'product' && status.textContent === copy.added) {
        document.querySelector('cart-drawer-component[auto-open]')?.open();
      }
    }
  }
}
customElements.define('sc-cross-selling', SCCrossSelling);